/**
 * ItemDetailDrawer — Right-side detail panel for SB to inspect / edit
 * a single Invoice or Expense, with embedded receipt preview, edit fields,
 * and the unified thread comments routed via `collab_threads`.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   item: the invoice/expense object
 *   itemType: 'invoice' | 'expense'
 *   clientId: string
 *   canEdit: boolean (Vollmacht)
 *   onChanged: () => void (parent re-fetches)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Check, MessageSquare, FileText, Receipt, Edit3, Save, X,
  CheckCircle2, AlertCircle, Lock, Send, ExternalLink,
  ChevronUp, ChevronDown, Boxes, Trash2, Ban, RotateCcw,
} from 'lucide-react';
import {
  getExpenseVatTotal, getExpenseNetTotal, getDisplayRates, isMultiTax,
  getInvoiceVatTotal, getInvoiceDisplayRates, isInvoiceMultiTax,
} from '@/utils/vatCalc';
import AssetActivationWizard from '@/components/anlagen/AssetActivationWizard';
import { getForeignRates } from '@/utils/vatForeign';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const advisorH = () => ({ Authorization: `Bearer ${localStorage.getItem('advisor_token')}` });

// Axios-Instanz mit SB-Token — für wiederverwendete FL-Komponenten (z.B. AfA-Wizard)
const advisorApi = axios.create({ baseURL: API });
advisorApi.interceptors.request.use((cfg) => {
  cfg.headers.Authorization = `Bearer ${localStorage.getItem('advisor_token')}`;
  return cfg;
});
const fmt = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '';

const EXPENSE_CATEGORIES = [
  { key: 'office', label: 'Büromaterial' }, { key: 'software', label: 'Software & Lizenzen' },
  { key: 'travel', label: 'Reisekosten' }, { key: 'hospitality', label: 'Bewirtung' },
  { key: 'vehicle', label: 'Fahrzeug & Fahrkosten' }, { key: 'telecom', label: 'Telefon & Internet' },
  { key: 'insurance', label: 'Versicherungen' }, { key: 'education', label: 'Fortbildung' },
  { key: 'marketing', label: 'Werbung & Marketing' }, { key: 'rent', label: 'Miete & Nebenkosten' },
  { key: 'consulting', label: 'Beratung (StB/Anwalt)' }, { key: 'equipment', label: 'Betriebsausstattung' },
  { key: 'other', label: 'Sonstiges' },
];
const catLabel = (key) => EXPENSE_CATEGORIES.find(c => c.key === key)?.label || key || '—';

const INVOICE_STATUSES = [
  { value: 'created', label: 'Erstellt' },
  { value: 'sent', label: 'Gesendet' },
  { value: 'paid', label: 'Bezahlt' },
  { value: 'overdue', label: 'Überfällig' },
  { value: 'cancelled', label: 'Storniert' },
];

export default function ItemDetailDrawer({ open, onClose, item: itemProp, itemType, clientId, canEdit, clientCountry = 'DE', clientIsPro = false, onChanged, onItemPatched, onPrev, onNext, hasPrev, hasNext }) {
  const [editMode, setEditMode] = useState(false);
  // Optimistischer Patch nach dem Speichern → neuer Wert ist SOFORT sichtbar,
  // ohne den Beleg schließen/neu öffnen zu müssen (Parent-Refetch ist verzögert).
  const [savedPatch, setSavedPatch] = useState({});
  const item = useMemo(
    () => (itemProp ? { ...itemProp, ...savedPatch } : itemProp),
    [itemProp, savedPatch],
  );
  useEffect(() => { setSavedPatch({}); }, [itemProp?.id]);
  const [form, setForm] = useState({});
  // Juni 2026 — Vorsteuerpauschalierung des Mandanten aktiv? (steuert Wareneinkauf/Fremdleistung-Kästchen)
  const [clientVstpActive, setClientVstpActive] = useState(false);
  useEffect(() => {
    if (!open || itemType !== 'expense' || !clientId) { setClientVstpActive(false); return; }
    let cancelled = false;
    axios.get(`${API}/advisor/clients/${clientId}/vorsteuer-pauschalierung`, { headers: advisorH() })
      .then(r => { if (!cancelled) setClientVstpActive(!!r.data?.vorsteuer_pauschalierung?.enabled); })
      .catch(() => { if (!cancelled) setClientVstpActive(false); });
    return () => { cancelled = true; };
  }, [open, itemType, clientId]);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false); // AfA-Aktivierung (Juni 2026)

  // Reset state when item changes
  useEffect(() => {
    if (!item) return;
    setEditMode(false);
    setReply('');
    setForm(itemType === 'invoice'
      ? {
          status: item.status || 'created',
          paid_at: (item.paid_at || '').slice(0, 10),
        }
      : {
          description: item.description || '',
          amount: item.amount ?? '',
          date: (item.date || '').slice(0, 10),
          // Default: paid_at spiegelt das Rechnungsdatum, falls nicht explizit gesetzt
          paid_at: (item.paid_at || item.date || '').slice(0, 10),
          category: item.category || 'other',
          vat_rate: item.vat_rate ?? 19,
          // Feb 2026 — Absetzbarer Anteil (SB-Override, z.B. Bewirtung 70/50/0 %)
          deductible_percentage: item.deductible_percentage ?? '',
          // Juni 2026 — Parität zum FL-Modal: voll/teilweise absetzbar (Privatanteil)
          tax_deductible_type: item.tax_deductible_type || (Number(item.deductible_percentage) < 100 ? 'partial' : 'full'),
          vendor: item.vendor || '',
          invoice_number: item.invoice_number || '',
          notes: item.notes || '',
          // Feb 2026 — Multi-Tax + Bewirtungsbescheinigung (SB editierbar, GoBD/Compliance)
          tax_breakdown: Array.isArray(item.tax_breakdown) ? item.tax_breakdown : [],
          multi_tax_suspected: !!item.multi_tax_suspected,
          hospitality_occasion: item.hospitality_occasion || '',
          hospitality_participants: item.hospitality_participants || '',
          hospitality_location: item.hospitality_location || '',
          // Feb 2026 — Reverse-Charge / Innergem. Erwerb (Eingangsrechnungen)
          is_reverse_charge: !!item.is_reverse_charge,
          is_intra_community_acquisition: !!item.is_intra_community_acquisition,
          rc_suspected: !!item.rc_suspected,
          supplier_country: item.supplier_country || '',
          supplier_vat_id: item.supplier_vat_id || '',
          // Juni 2026 — Vorsteuerpauschalierung: Wareneinkauf/Fremdleistung-Markierung (SB editierbar)
          vstp_sonder_vorsteuer: !!item.vstp_sonder_vorsteuer,
        });
  }, [item?.id, itemType]);

  // Load preview (receipt for expense, invoice PDF for invoice)
  useEffect(() => {
    if (!item || !open) return;
    let cancelled = false;
    setPreviewBlob(null); setPreviewError(null);
    const load = async () => {
      try {
        setPreviewLoading(true);
        let url, isPdf = false;
        if (itemType === 'invoice') {
          url = `${API}/advisor/clients/${clientId}/invoice-pdf/${item.id}`;
          isPdf = true;
        } else if (item.has_receipt) {
          url = `${API}/advisor/clients/${clientId}/receipt/${item.id}`;
        } else {
          setPreviewError('Kein Beleg vorhanden');
          return;
        }
        const res = await axios.get(url, { headers: advisorH(), responseType: 'blob' });
        if (cancelled) return;
        const blob = new Blob([res.data], { type: res.headers['content-type'] || (isPdf ? 'application/pdf' : 'image/jpeg') });
        setPreviewBlob({ url: URL.createObjectURL(blob), type: blob.type });
      } catch (err) {
        if (!cancelled) setPreviewError(err.response?.status === 404 ? 'Vorschau nicht verfügbar' : 'Fehler beim Laden der Vorschau');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (previewBlob?.url) URL.revokeObjectURL(previewBlob.url);
    };
  }, [item?.id, itemType, open]);

  // Load thread for this item
  const loadThread = useCallback(async () => {
    if (!item?.id) return;
    try {
      // Find or create using item-comments endpoint (returns linked thread_id)
      const r = await axios.get(`${API}/advisor/comments/for-${itemType}/${item.id}`, { headers: advisorH() });
      const comments = r.data.comments || [];
      const tid = comments[0]?.thread_id;
      if (tid) {
        const t = await axios.get(`${API}/collab/threads/${tid}`, { headers: advisorH() });
        setThread(t.data.thread); setMessages(t.data.messages || []);
      } else {
        setThread(null); setMessages([]);
      }
    } catch {
      setThread(null); setMessages([]);
    }
  }, [item?.id, itemType]);

  useEffect(() => { if (open && item) loadThread(); }, [open, item?.id, loadThread]);

  // Keyboard shortcuts (P3, Feb 2026: erweitert um Edit-Mode-Field-Shortcuts + Help-Overlay)
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const focusField = (testId) => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el) return;
      // SelectTrigger ist ein <button>, .click() öffnet das Dropdown.
      if (el.tagName === 'BUTTON') el.click();
      else el.focus();
    };

    const handler = (e) => {
      const inInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';

      // „?" toggelt Help-Overlay (außerhalb von Inputs)
      if (e.key === '?' && !inInput) {
        e.preventDefault();
        setHelpOpen(prev => !prev);
        return;
      }
      if (helpOpen) {
        if (e.key === 'Escape') { setHelpOpen(false); return; }
        return; // Lock weiterer Shortcuts solange Help offen
      }

      // Enter in Edit-Mode → Save (auch aus Eingabefeldern heraus; Textarea & Shift+Enter ausgenommen)
      if (editMode && e.key === 'Enter' && !e.shiftKey) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag !== 'textarea') {
          e.preventDefault();
          handleSaveRef.current && handleSaveRef.current();
          return;
        }
      }

      if (inInput) return;

      if (e.key === 'Escape') {
        if (editMode) setEditMode(false);
        else onClose();
      }
      else if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); onNext && onNext(); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); onPrev && onPrev(); }
      else if (e.key === 'r' && !editMode) { e.preventDefault(); handleReviewToggle(); }
      else if (e.key === 'e' && !editMode && canEdit) { e.preventDefault(); setEditMode(true); }
      // Edit-Mode Field-Shortcuts (Shift+Buchstabe — kein Konflikt mit j/k/r-Navigation)
      else if (editMode && e.key === 'B') { e.preventDefault(); focusField('drawer-edit-amount'); }
      else if (editMode && e.key === 'K') { e.preventDefault(); focusField('drawer-edit-category'); }
      else if (editMode && e.key === 'D') { e.preventDefault(); focusField('drawer-edit-date'); }
      else if (editMode && e.key === 'P') { e.preventDefault(); focusField('drawer-edit-paid-at'); }
      else if (editMode && e.key === 'Enter') { e.preventDefault(); handleSaveRef.current && handleSaveRef.current(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line
  }, [open, editMode, helpOpen, onNext, onPrev, canEdit]);

  const handleSave = async () => {
    if (!canEdit) return;
    setBusy(true);
    try {
      if (itemType === 'invoice') {
        // Persist status
        if (form.status !== item.status) {
          await axios.put(`${API}/advisor/clients/${clientId}/invoice/${item.id}/status`,
            { status: form.status }, { headers: advisorH() });
        }
        // Persist paid_at (with auto-status logic on backend side)
        const newPaidAt = form.paid_at ? form.paid_at : null;
        const oldPaidAt = (item.paid_at || '').slice(0, 10) || null;
        if (newPaidAt !== oldPaidAt) {
          await axios.put(`${API}/advisor/clients/${clientId}/invoice/${item.id}/paid-at`,
            { paid_at: newPaidAt }, { headers: advisorH() });
        }
      } else {
        const updates = { ...form };
        if (updates.amount !== '') updates.amount = Math.round(parseFloat(updates.amount) * 100) / 100;
        // Sende paid_at nur, wenn explizit gesetzt; leerer String → null
        if (updates.paid_at === '') updates.paid_at = null;
        // deductible_percentage: leerer String → nicht senden (Backend würde float('') werfen)
        if (updates.deductible_percentage === '' || updates.deductible_percentage === null || updates.deductible_percentage === undefined) {
          delete updates.deductible_percentage;
        }
        // USt konsistent zum (neuen) Brutto: bei Einzelsatz (kein Multi-Tax)
        // net_amount / vat_amount / Einzel-tax_breakdown aus Brutto + Satz neu
        // berechnen, damit die angezeigte USt dem geänderten Bruttowert folgt.
        const tb = Array.isArray(form.tax_breakdown) ? form.tax_breakdown : [];
        if (tb.length < 2) {
          const gross = typeof updates.amount === 'number' ? updates.amount : (parseFloat(updates.amount) || 0);
          const r = parseFloat(form.vat_rate) || 0;
          const net = r > 0 ? Math.round((gross / (1 + r / 100)) * 100) / 100 : gross;
          const vat = Math.round((gross - net) * 100) / 100;
          updates.net_amount = net;
          updates.vat_amount = vat;
          updates.tax_breakdown = r > 0 ? [{ rate: r, net, vat }] : [];
        }
        await axios.put(`${API}/advisor/clients/${clientId}/expense/${item.id}`,
          updates, { headers: advisorH() });
        // Anzeige sofort aktualisieren (optimistisch), bis der Parent-Refetch greift.
        setSavedPatch((p) => ({ ...p, ...updates }));
      }
      toast.success('Gespeichert');
      setEditMode(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setBusy(false);
  };
  // Immer die aktuellste handleSave-Closure (frisches form) für die Tastatur-Handler.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  const handleDeactivate = async () => {
    if (isInvoice || !item?.id) return;
    if (item.is_locked) { toast.error('Festgeschriebene Periode — nicht änderbar'); return; }
    if (item.treatment === 'capitalized') {
      toast.error('Diese Ausgabe ist als Anlage aktiviert — bitte zuerst die Anlage entfernen.');
      return;
    }
    const reason = window.prompt(
      `Ausgabe "${(item.description || item.vendor || 'Ausgabe')}" deaktivieren.\n\nBitte einen Grund angeben (Pflicht). Der Beleg bleibt sichtbar (ausgegraut), zählt aber nicht mehr in UVA, Gewinn, Abschluss oder Export.`
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben.'); return; }
    setReviewing(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/expense/${item.id}/deactivate`,
        { reason: reason.trim() }, { headers: advisorH() });
      toast.success('Ausgabe deaktiviert');
      onChanged && onChanged();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deaktivieren fehlgeschlagen');
    }
    setReviewing(false);
  };

  const handleReactivate = async () => {
    if (isInvoice || !item?.id) return;
    if (item.is_locked) { toast.error('Festgeschriebene Periode — nicht änderbar'); return; }
    setReviewing(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/expense/${item.id}/reactivate`,
        {}, { headers: advisorH() });
      toast.success('Ausgabe reaktiviert');
      onChanged && onChanged();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reaktivieren fehlgeschlagen');
    }
    setReviewing(false);
  };


  const handleReviewToggle = async () => {
    if (item?.is_locked) { toast.error('Festgeschriebene Periode — nicht änderbar'); return; }
    const prevReviewed = !!item.reviewed_by_advisor;
    const optimistic = {
      reviewed_by_advisor: !prevReviewed,
      reviewed_at: prevReviewed ? null : new Date().toISOString(),
    };
    // SOFORT spiegeln (vor dem Netzwerk-Roundtrip) — Drawer + Listenzeile.
    setSavedPatch((p) => ({ ...p, ...optimistic }));
    onItemPatched && onItemPatched(item.id, optimistic);
    setReviewing(true);
    try {
      const res = await axios.post(`${API}/advisor/clients/${clientId}/${itemType}/${item.id}/review-toggle`,
        {}, { headers: advisorH() });
      // Server-Wahrheit übernehmen (z.B. Name/Zeitstempel).
      const confirmed = {
        reviewed_by_advisor: res.data.reviewed_by_advisor,
        reviewed_at: res.data.reviewed_at,
        reviewed_by_advisor_name: res.data.reviewed_by_advisor_name,
      };
      setSavedPatch((p) => ({ ...p, ...confirmed }));
      onItemPatched && onItemPatched(item.id, confirmed);
      toast.success(res.data.reviewed_by_advisor ? 'Als geprüft markiert' : 'Prüfung zurückgesetzt');
      onChanged && onChanged();
    } catch (err) {
      // Rollback auf vorherigen Stand.
      const rollback = { reviewed_by_advisor: prevReviewed, reviewed_at: item.reviewed_at || null };
      setSavedPatch((p) => ({ ...p, ...rollback }));
      onItemPatched && onItemPatched(item.id, rollback);
      toast.error(err.response?.data?.detail || 'Fehler');
    }
    setReviewing(false);
  };

  const handleSendComment = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      if (thread) {
        // Reply to existing thread
        const r = await axios.post(`${API}/collab/threads/${thread.id}/messages`,
          { body: reply.trim() }, { headers: advisorH() });
        setMessages(m => [...m, r.data.message]);
        setThread(r.data.thread);
      } else {
        // Use legacy comments endpoint which auto-creates a thread
        await axios.post(`${API}/advisor/clients/${clientId}/comments`,
          { entity_type: itemType, entity_id: item.id, text: reply.trim() },
          { headers: advisorH() });
        await loadThread();
      }
      setReply('');
      toast.success('Anmerkung gesendet — landet in der FL-Inbox');
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
    setBusy(false);
  };

  const handleResolveThread = async () => {
    if (!thread) return;
    try {
      await axios.patch(`${API}/collab/threads/${thread.id}`,
        { status: 'done' }, { headers: advisorH() });
      toast.success('Vorgang erledigt');
      await loadThread();
      onChanged && onChanged();
    } catch { toast.error('Fehler'); }
  };

  if (!item) return null;

  // Computed display values
  const isInvoice = itemType === 'invoice';
  const isReviewed = !!item.reviewed_by_advisor;
  const hasOpenThread = (item.open_thread_count || 0) > 0;

  const title = isInvoice
    ? `Rechnung ${item.invoice_number || ''}`
    : `Ausgabe: ${item.description || item.vendor || '—'}`;

  // Brutto/Netto/USt for display
  // Feb 2026 — Multi-VAT-Fix: nutzt zentralen vatCalc-Helper.
  // Bei Multi-Tax-Ausgaben (tax_breakdown[] ≥2): Σ aus breakdown statt Single-Rate-Math.
  // Bei Multi-Tax-Rechnungen (items mit verschiedenen tax_rate): rate-Liste statt blended rate.
  const bruttoVal = isInvoice ? (item.total || 0) : (item.amount || 0);
  const ustVal = isInvoice ? getInvoiceVatTotal(item) : getExpenseVatTotal(item);
  const nettoVal = isInvoice
    ? (item.subtotal || +(bruttoVal - ustVal).toFixed(2))
    : getExpenseNetTotal(item);
  const displayRates = isInvoice ? getInvoiceDisplayRates(item) : getDisplayRates(item);
  const isMulti = isInvoice ? isInvoiceMultiTax(item) : isMultiTax(item);
  const taxRateLabel = isMulti
    ? `gesamt (${displayRates.join(' + ')})`
    : (displayRates[0] || '');

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right"
        className="w-full sm:max-w-3xl lg:max-w-5xl p-0 flex flex-col bg-zinc-50"
        data-testid="item-detail-drawer">
        <SheetHeader className="px-5 py-3 bg-white border-b border-zinc-200 sticky top-0 z-10 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold text-slate-900 flex items-center gap-2 truncate">
                {isInvoice ? <FileText className="w-4 h-4 text-emerald-600 shrink-0" /> : <Receipt className="w-4 h-4 text-rose-600 shrink-0" />}
                <span className="truncate">{title}</span>
                {item.is_locked && <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title={`Festgeschrieben: ${item.locked_period_label || ''}`} />}
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-500 mt-0.5">
                {isInvoice
                  ? `${item.customer_name || '—'} · ${fmtDate(item.invoice_date || item.date)} · ${fmt(bruttoVal)} €`
                  : `${catLabel(item.category)} · ${fmtDate(item.date)} · ${fmt(bruttoVal)} €`}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={onPrev} disabled={!hasPrev} title="Vorheriger Eintrag (k / ↑)"
                className="p-1.5 border border-zinc-200 rounded text-slate-500 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="drawer-prev-btn">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button onClick={onNext} disabled={!hasNext} title="Nächster Eintrag (j / ↓)"
                className="p-1.5 border border-zinc-200 rounded text-slate-500 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="drawer-next-btn">
                <ChevronDown className="w-4 h-4" />
              </button>
              <button onClick={() => setHelpOpen(true)} title="Tastenkürzel anzeigen (?)"
                className="p-1.5 border border-zinc-200 rounded text-slate-500 hover:bg-zinc-50"
                data-testid="drawer-help-btn">
                <span className="text-xs font-mono leading-none">?</span>
              </button>
              <button onClick={onClose} title="Schließen (Esc)"
                className="p-1.5 border border-zinc-200 rounded text-slate-500 hover:bg-zinc-50"
                data-testid="drawer-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between gap-2 mt-1 -mx-1 px-1">
            <div className="flex items-center gap-2">
              <button
                onClick={handleReviewToggle}
                disabled={reviewing || item.is_locked}
                title="Tastenkürzel: r"
                className={`text-xs px-2.5 py-1 border rounded-md flex items-center gap-1.5 font-medium transition-colors disabled:opacity-50 ${
                  isReviewed
                    ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                    : hasOpenThread
                      ? 'bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100'
                      : 'bg-white text-slate-700 border-zinc-300 hover:bg-zinc-50'
                }`}
                data-testid="drawer-review-toggle"
              >
                {isReviewed
                  ? <><CheckCircle2 className="w-3.5 h-3.5" />Geprüft{item.reviewed_at && ` · ${fmtDate(item.reviewed_at)}`}</>
                  : hasOpenThread
                    ? <><AlertCircle className="w-3.5 h-3.5" />Rückfrage offen ({item.open_thread_count})</>
                    : <><Check className="w-3.5 h-3.5" />Als geprüft markieren</>}
                <kbd className={`ml-1 px-1 text-[9px] font-mono rounded border ${
                  isReviewed ? 'bg-emerald-700 border-emerald-500 text-emerald-100' : 'bg-zinc-100 border-zinc-300 text-slate-500'
                }`}>r</kbd>
              </button>
              {canEdit && !item.is_locked && (
                <button
                  onClick={() => setEditMode(e => !e)}
                  className={`text-xs px-2.5 py-1 border rounded-md flex items-center gap-1.5 font-medium ${
                    editMode ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-zinc-300 hover:bg-zinc-50'
                  }`}
                  data-testid="drawer-edit-toggle"
                  title={editMode ? 'Ansicht (Esc)' : 'Bearbeiten (Tastenkürzel: E)'}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  {editMode ? 'Ansicht' : 'Bearbeiten'}
                  {!editMode && (
                    <kbd className="ml-1 px-1 py-0 text-[10px] font-mono bg-zinc-100 text-slate-500 border border-zinc-300 rounded">
                      E
                    </kbd>
                  )}
                </button>
              )}
              {!canEdit && (
                <span className="text-[11px] text-slate-500 italic">Nur Kommentar möglich (keine Vollmacht).</span>
              )}
              {/* Juni 2026 — AfA-Aktivierung aus dem Beleg (mit Vollmacht, Mandant Pro) */}
              {!isInvoice && canEdit && !item.is_locked && clientIsPro && item.treatment !== 'capitalized' && (
                <button
                  onClick={() => setActivationOpen(true)}
                  className="text-xs px-2.5 py-1 border rounded-md flex items-center gap-1.5 font-medium bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid="drawer-activate-asset-btn"
                  title="Diesen Beleg als Anlagegut aktivieren (AfA)"
                >
                  <Boxes className="w-3.5 h-3.5" />Als Anlage aktivieren
                </button>
              )}
              {!isInvoice && item.treatment === 'capitalized' && (
                <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 flex items-center gap-1" data-testid="drawer-already-capitalized">
                  <Boxes className="w-3 h-3" />Als Anlage aktiviert
                </span>
              )}
              {!isInvoice && canEdit && !item.is_locked && item.treatment !== 'capitalized' && !item.deactivated_at && (
                <button
                  onClick={handleDeactivate}
                  disabled={reviewing}
                  className="text-xs px-2.5 py-1 border rounded-md flex items-center gap-1.5 font-medium bg-white text-amber-700 border-amber-300 hover:bg-amber-50 disabled:opacity-50"
                  data-testid="drawer-deactivate-expense-btn"
                  title="Ausgabe deaktivieren (z.B. Duplikat/Fehlbuchung) — bleibt sichtbar, zählt aber nicht mehr"
                >
                  <Ban className="w-3.5 h-3.5" />Deaktivieren
                </button>
              )}
              {!isInvoice && canEdit && !item.is_locked && item.deactivated_at && (
                <button
                  onClick={handleReactivate}
                  disabled={reviewing}
                  className="text-xs px-2.5 py-1 border rounded-md flex items-center gap-1.5 font-medium bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-50"
                  data-testid="drawer-reactivate-expense-btn"
                  title="Ausgabe wieder aktivieren"
                >
                  <RotateCcw className="w-3.5 h-3.5" />Reaktivieren
                </button>
              )}
            </div>
            {/* Keyboard shortcut legend */}
            <div className="hidden sm:flex items-center gap-2 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><kbd className="px-1 bg-zinc-100 border border-zinc-300 rounded font-mono">↑↓</kbd>/<kbd className="px-1 bg-zinc-100 border border-zinc-300 rounded font-mono">jk</kbd> Nav</span>
              <span className="flex items-center gap-1"><kbd className="px-1 bg-zinc-100 border border-zinc-300 rounded font-mono">r</kbd> Geprüft</span>
              {canEdit && (
                <span className="flex items-center gap-1"><kbd className="px-1 bg-zinc-100 border border-zinc-300 rounded font-mono">e</kbd> Bearbeiten</span>
              )}
              <span className="flex items-center gap-1"><kbd className="px-1 bg-zinc-100 border border-zinc-300 rounded font-mono">Esc</kbd> Schließen</span>
            </div>
          </div>
        </SheetHeader>

        {/* Body: Split View */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left: Preview */}
          <div className="bg-zinc-100 border-r border-zinc-200 overflow-auto p-4 flex items-start justify-center">
            {previewLoading && <div className="text-xs text-slate-500 py-12">Lädt Vorschau...</div>}
            {previewError && (
              <div className="text-center py-16">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <div className="text-xs text-slate-500">{previewError}</div>
              </div>
            )}
            {previewBlob && previewBlob.type === 'application/pdf' && (
              <iframe src={previewBlob.url} title="Beleg-Vorschau"
                className="w-full h-full min-h-[60vh] bg-white border border-zinc-200 rounded shadow"
                data-testid="drawer-pdf-preview" />
            )}
            {previewBlob && previewBlob.type !== 'application/pdf' && (
              <img src={previewBlob.url} alt="Beleg-Vorschau"
                className="max-w-full max-h-[80vh] object-contain border border-zinc-200 rounded shadow bg-white"
                data-testid="drawer-img-preview" />
            )}
          </div>

          {/* Right: Form + Thread */}
          <div className="overflow-auto p-5 bg-white">
            {!isInvoice && item.deactivated_at && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="drawer-deactivated-banner">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <Ban className="w-4 h-4" />Deaktivierte Ausgabe
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  Zählt nicht mehr in UVA, Gewinn (EÜR), Periodenabschluss oder Export.
                  {item.deactivated_reason ? <> Grund: „{item.deactivated_reason}".</> : null}
                </p>
                {(item.deactivated_by_name || item.deactivated_at) && (
                  <p className="text-[11px] text-amber-600 mt-0.5">
                    {item.deactivated_by_name ? `${item.deactivated_by_name} · ` : ''}
                    {item.deactivated_at ? new Date(item.deactivated_at).toLocaleString('de-DE') : ''}
                  </p>
                )}
              </div>
            )}
            {/* Brutto / Netto / USt summary */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded p-2.5">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Brutto</div>
                <div className={`text-base font-bold tabular-nums ${isInvoice ? 'text-emerald-700' : 'text-rose-700'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {isInvoice ? '+' : '−'}{fmt(bruttoVal)} €
                </div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded p-2.5">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Netto</div>
                <div className="text-base font-medium text-slate-700 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmt(nettoVal)} €</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded p-2.5">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">USt {taxRateLabel}</div>
                <div className="text-base font-medium text-blue-700 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="drawer-ust-total">{fmt(ustVal)} €</div>
              </div>
            </div>

            {/* Detail Form */}
            {isInvoice ? (
              <InvoiceDetailFields item={item} editMode={editMode} canEdit={canEdit} form={form} setForm={setForm} />
            ) : (
              <ExpenseDetailFields item={item} editMode={editMode} canEdit={canEdit} form={form} setForm={setForm} clientCountry={clientCountry} vstpActive={clientVstpActive} />
            )}

            {editMode && (
              <div className="flex justify-end gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={() => setEditMode(false)} className="rounded-md text-xs h-8">Abbrechen</Button>
                <Button size="sm" onClick={handleSave} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs h-8" data-testid="drawer-save-btn">
                  <Save className="w-3 h-3 mr-1" />Speichern
                </Button>
              </div>
            )}

            {/* Audit info */}
            {item.modified_by_advisor && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3 flex items-start gap-1.5">
                <Edit3 className="w-3 h-3 mt-0.5 shrink-0" />
                Zuletzt geändert von {item.modified_by_advisor_name || 'SB'} · {fmtDateTime(item.modified_by_advisor_at)}
              </div>
            )}
            {item.last_modified_by_advisor && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3 flex items-start gap-1.5">
                <Edit3 className="w-3 h-3 mt-0.5 shrink-0" />
                Status zuletzt geändert von {item.last_modified_by_advisor_name || 'SB'} · {fmtDateTime(item.last_modified_by_advisor_at)}
              </div>
            )}

            {/* Thread / Comments */}
            <div className="border-t border-zinc-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Anmerkungen & Rückfragen
                  {thread?.status === 'open' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">offen</span>
                  )}
                </div>
                {thread && thread.status === 'open' && (
                  <button onClick={handleResolveThread}
                    className="text-[11px] px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                    data-testid="drawer-resolve-thread">
                    <Check className="w-3 h-3 inline mr-0.5" />Erledigt
                  </button>
                )}
              </div>

              {messages.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-2">Noch keine Anmerkungen.</div>
              ) : (
                <div className="space-y-2 mb-3 max-h-96 overflow-auto">
                  {messages.map(m => (
                    <div key={m.id} className={`p-2.5 rounded text-sm border ${
                      m.author_type === 'advisor'
                        ? 'bg-blue-50/60 border-blue-100'
                        : 'bg-emerald-50/60 border-emerald-100'
                    }`}>
                      <div className="text-[10px] text-slate-500 mb-0.5">
                        {m.author_name || m.author_type} · {fmtDateTime(m.created_at)}
                      </div>
                      <div className="text-slate-800 whitespace-pre-wrap">{m.body}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={2}
                  placeholder={hasOpenThread ? 'Antwort hinzufügen...' : 'Anmerkung an den Mandanten...'}
                  className="flex-1 text-sm resize-none"
                  data-testid="drawer-reply-input"
                />
                <Button onClick={handleSendComment} disabled={!reply.trim() || busy}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs self-end" data-testid="drawer-send-btn">
                  <Send className="w-3 h-3 mr-1" />Senden
                </Button>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                Diese Anmerkung landet im zentralen Posteingang des Mandanten und verlinkt direkt zu diesem {isInvoice ? 'Rechnungseintrag' : 'Beleg'}.
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
      {helpOpen && (
        <ShortcutsHelpOverlay editMode={editMode} canEdit={canEdit} onClose={() => setHelpOpen(false)} />
      )}
      {/* Juni 2026 — AfA-Aktivierungs-Wizard (wiederverwendet aus FL, SB-Endpoints) */}
      {!isInvoice && (
        <AssetActivationWizard
          open={activationOpen}
          onClose={() => setActivationOpen(false)}
          expense={item}
          country={clientCountry}
          apiClient={advisorApi}
          afaTableUrl={`/advisor/clients/${clientId}/afa-table`}
          previewUrl={`/advisor/clients/${clientId}/fixed-assets/preview-plan`}
          activateUrl={`/advisor/clients/${clientId}/expenses/${item.id}/activate-as-asset`}
          notifyOption={true}
          onActivated={() => {
            setActivationOpen(false);
            onChanged && onChanged();
          }}
        />
      )}
    </Sheet>
  );
}

// P3 (Feb 2026): Help-Overlay listet alle Drawer-Shortcuts kontextabhängig.
function ShortcutsHelpOverlay({ editMode, canEdit, onClose }) {
  const navShortcuts = [
    { keys: ['?'], label: 'Diese Hilfe öffnen / schließen' },
    { keys: ['Esc'], label: editMode ? 'Bearbeiten verlassen' : 'Drawer schließen' },
    { keys: ['j', '↓'], label: 'Nächster Beleg' },
    { keys: ['k', '↑'], label: 'Vorheriger Beleg' },
    { keys: ['r'], label: 'Als geprüft markieren / zurücksetzen' },
  ];
  if (canEdit && !editMode) navShortcuts.push({ keys: ['e'], label: 'Bearbeiten-Modus aktivieren' });

  const editShortcuts = [
    { keys: ['Shift', 'B'], label: 'Betrag fokussieren' },
    { keys: ['Shift', 'K'], label: 'Kategorie öffnen' },
    { keys: ['Shift', 'D'], label: 'Datum fokussieren' },
    { keys: ['Shift', 'P'], label: 'Bezahlt am fokussieren' },
    { keys: ['Enter'], label: 'Speichern (außerhalb von Eingabefeldern)' },
    { keys: ['⌘/Ctrl', 'Enter'], label: 'Speichern (auch aus Eingabefeldern)' },
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose} data-testid="drawer-help-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Tastatur-Shortcuts</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="drawer-help-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Navigation</div>
        <div className="space-y-1.5 mb-4">
          {navShortcuts.map((s, i) => <ShortcutRow key={i} {...s} />)}
        </div>

        {editMode && (
          <>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Bearbeiten</div>
            <div className="space-y-1.5">
              {editShortcuts.map((s, i) => <ShortcutRow key={i} {...s} />)}
            </div>
          </>
        )}

        <div className="mt-5 pt-4 border-t border-slate-200 text-[11px] text-slate-500">
          Tipp: Diese Hilfe lässt sich jederzeit mit <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[10px]">?</kbd> öffnen.
        </div>
      </div>
    </div>
  );
}

function ShortcutRow({ keys, label }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700">{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-slate-300 text-xs">+</span>}
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono text-[11px] text-slate-700">{k}</kbd>
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

// ─────────── Invoice Fields ───────────

function InvoiceDetailFields({ item, editMode, canEdit, form, setForm }) {
  return (
    <div className="space-y-2 text-sm" data-testid="drawer-invoice-fields">
      <FieldRow label="Rechnungsnummer" value={item.invoice_number} />
      <FieldRow label="Kunde" value={item.customer_name} />
      <FieldRow label="Datum" value={fmtDate(item.invoice_date || item.date)} />
      <FieldRow label="Fällig" value={fmtDate(item.due_date)} />
      <FieldRow label="Leistungsdatum" value={fmtDate(item.service_date)} />

      {editMode && canEdit ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="h-8 text-sm rounded-md" data-testid="drawer-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{INVOICE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Bezahlt am</Label>
            <Input type="date" value={form.paid_at || ''}
              onChange={e => setForm(f => ({ ...f, paid_at: e.target.value }))}
              className="h-8 text-sm rounded-md" data-testid="drawer-paid-at-input" />
            <div className="text-[10px] text-slate-400 mt-0.5">Setzt automatisch Status „Bezahlt"; Leerlassen entfernt das Datum.</div>
          </div>
        </div>
      ) : (
        <>
          <FieldRow label="Status" value={INVOICE_STATUSES.find(s => s.value === item.status)?.label || item.status} highlight={item.status === 'paid'} />
          <FieldRow label="Bezahlt am" value={fmtDate(item.paid_at)} highlight={!!item.paid_at} />
        </>
      )}

      {item.notes && <FieldRow label="Notizen" value={item.notes} multiline />}
      <FieldRow label="Land" value={item.country} />
    </div>
  );
}

// ─────────── Expense Fields ───────────

function ExpenseDetailFields({ item, editMode, canEdit, form, setForm, clientCountry, vstpActive = false }) {
  if (!editMode || !canEdit) {
    const paidDiffers = item.paid_at && item.date && (item.paid_at.slice(0, 10) !== item.date.slice(0, 10));
    // Feb 2026 — Multi-Tax-Wahrheit ist STRIKT tax_breakdown.length >= 2 (siehe FL-Detail-View).
    // Das frühere `multi_tax_suspected`-Flag wird ignoriert, da AI es nicht zuverlässig setzt.
    const isMultiTax = Array.isArray(item.tax_breakdown) && item.tax_breakdown.length >= 2;
    const isHospitality = item.category === "hospitality";
    return (
      <div className="space-y-2 text-sm" data-testid="drawer-expense-fields">
        <FieldRow label="Beschreibung" value={item.description} />
        <FieldRow label="Händler" value={item.vendor} />
        <FieldRow label="Rechnungsdatum" value={fmtDate(item.date)} />
        <FieldRow
          label="Bezahlt am"
          value={item.paid_at ? fmtDate(item.paid_at) : (item.date ? `${fmtDate(item.date)} (= Rechnungsdatum)` : '—')}
          highlight={paidDiffers}
        />
        <FieldRow label="Kategorie" value={catLabel(item.category)} />
        {/* Feb 2026 — Single-Tax: normaler MwSt-Satz; Multi-Tax: stattdessen Aufschlüsselungs-Tabelle */}
        {!isMultiTax && (
          <FieldRow label="MwSt-Satz" value={item.vat_rate == null ? '—' : (Number(item.vat_rate) === 0 ? '0 % (steuerfrei)' : `${item.vat_rate}%`)} />
        )}
        {(() => {
          const foreign = getForeignRates(item, clientCountry);
          if (!foreign.length) return null;
          const cc = (clientCountry || 'DE').toUpperCase();
          return (
            <div className="my-2 rounded border border-amber-300 bg-amber-50 p-2" data-testid="drawer-foreign-vat-hint-readonly">
              <p className="text-[11px] text-amber-800">
                <strong>Ausländischer Steuersatz</strong> ({foreign.map(r => `${r}%`).join(', ')}): nicht als Vorsteuer in der {cc}-Voranmeldung abziehbar — der Bruttobetrag zählt als Betriebsausgabe (ggf. EU-Vorsteuererstattungsverfahren). Falls RC-fähig: korrigierte Rechnung beim Lieferanten anfordern.
              </p>
            </div>
          );
        })()}
        {isMultiTax && (
          <div className="py-2 border-b border-zinc-100" data-testid="drawer-multi-tax-readonly">
            <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1.5">
              <span className="text-amber-600">⚠</span>
              Mehrere MwSt-Sätze · GoBD-konform aufgesplittet
            </div>
            <table className="w-full text-xs border border-amber-200 rounded overflow-hidden">
              <thead className="bg-amber-50 text-amber-900">
                <tr>
                  <th className="px-2 py-1 text-left font-semibold">Satz</th>
                  <th className="px-2 py-1 text-right font-semibold">Netto</th>
                  <th className="px-2 py-1 text-right font-semibold">MwSt</th>
                  <th className="px-2 py-1 text-right font-semibold">Brutto</th>
                </tr>
              </thead>
              <tbody>
                {item.tax_breakdown.map((tb, i) => {
                  const net = parseFloat(tb.net) || 0;
                  const vat = parseFloat(tb.vat) || 0;
                  return (
                    <tr key={i} className="border-t border-amber-100">
                      <td className="px-2 py-1 font-medium">{tb.rate}%</td>
                      <td className="px-2 py-1 text-right tabular-nums">{net.toFixed(2)} €</td>
                      <td className="px-2 py-1 text-right tabular-nums">{vat.toFixed(2)} €</td>
                      <td className="px-2 py-1 text-right tabular-nums font-medium">{(net + vat).toFixed(2)} €</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <FieldRow label="Rechnungsnr." value={item.invoice_number} />
        {/* Aug 2026 — Drittland-Steuer: kein Vorsteuerabzug (Hinweis für SB) */}
        {item.foreign_vat_blocked ? (
          <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-2" data-testid="drawer-foreign-vat-blocked">
            <div className="text-xs font-semibold text-orange-900">⚠️ Ausländische Steuer — kein Vorsteuerabzug</div>
            <div className="text-[11px] text-orange-800 mt-0.5 leading-snug">
              Drittland-Beleg mit ausgewiesener Steuer{item.foreign_tax_amount ? ` (${Number(item.foreign_tax_amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)` : ''} —
              als Kostenbestandteil verbucht (Brutto = Betriebsausgabe, kein KZ-Abzug in der UVA).
            </div>
          </div>
        ) : null}
        {/* Aug 2026 — Währungsumrechnung (FX-Audit) read-only */}
        {item.original_currency && item.fx_rate ? (
          <div className="py-2 border-b border-zinc-100" data-testid="drawer-fx-readonly">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Währungsumrechnung</span>
              <span className="text-sm text-slate-800 tabular-nums text-right">
                {Number(item.original_amount || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.original_currency} × {Number(item.fx_rate).toLocaleString('de-DE', { maximumFractionDigits: 6 })} = {Number(item.amount || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
            {item.fx_rate_date ? (
              <div className="text-[10px] text-slate-400 mt-0.5 text-right">EZB-Referenzkurs vom {new Date(item.fx_rate_date).toLocaleDateString('de-DE')}</div>
            ) : null}
          </div>
        ) : null}
        {/* Feb 2026 — Absetzbarer Anteil + SB-Override-Hinweis */}
        {(item.deductible_percentage !== undefined && item.deductible_percentage !== null && item.deductible_percentage !== '') && (
          <div className="py-2 border-b border-zinc-100" data-testid="drawer-deductible-readonly">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Absetzbarer Anteil</span>
              <span className={`text-sm font-medium tabular-nums ${Number(item.deductible_percentage) < 100 ? 'text-amber-700' : 'text-slate-800'}`}>
                {Number(item.deductible_percentage)}%
              </span>
            </div>
            {item.modified_by_advisor && (
              <div className="mt-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-block" data-testid="drawer-deductible-sb-override">
                SB-korrigierter Wert{item.modified_by_advisor_name ? ` · ${item.modified_by_advisor_name}` : ''}
              </div>
            )}
          </div>
        )}
        {item.notes && <FieldRow label="Notizen" value={item.notes} multiline />}
        <FieldRow label="Beleg" value={item.has_receipt ? 'Vorhanden' : 'Fehlt'} highlight={!item.has_receipt} />

        {/* Feb 2026 — Bewirtungsbescheinigung (§4 EStG/§20 EStG) — read-only für SB */}
        {isHospitality && (
          <div className="mt-3 rounded-md border-2 border-amber-200 bg-amber-50/60 p-3" data-testid="drawer-hospitality-readonly">
            <div className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1.5">
              📋 Bewirtungsbescheinigung
              {(!item.hospitality_occasion || !item.hospitality_participants || !item.hospitality_location) && (
                <span className="text-rose-700 font-bold">— UNVOLLSTÄNDIG</span>
              )}
            </div>
            <div className="space-y-1">
              <FieldRow label="Anlass" value={item.hospitality_occasion} />
              <FieldRow label="Teilnehmer" value={item.hospitality_participants} multiline />
              <FieldRow label="Ort" value={item.hospitality_location} />
            </div>
          </div>
        )}

        {/* Feb 2026 — Steuerart-Anzeige (RC / Innergem. Erwerb), nur bei non-hospitality
            Erweiterung Feb 2026: zeigt SB beim Belegcheck den exakten Compliance-Kontext
            inkl. UStVA-Kennzahl-Hinweis (analog zur Outbound-Rechnung „1:1 wie im PDF").
            Inbound-RC und Inbound-IGE haben unterschiedliche Buchungs-Logik, deshalb
            getrennte Hinweise. */}
        {!isHospitality && (item.is_reverse_charge || item.is_intra_community_acquisition || item.rc_suspected) && (
          <div
            className={`mt-3 rounded-md border-2 p-3 ${item.rc_suspected && !item.is_reverse_charge && !item.is_intra_community_acquisition ? "border-amber-300 bg-amber-50/60" : "border-emerald-300 bg-emerald-50/60"}`}
            data-testid="drawer-tax-scheme-readonly"
          >
            <div className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              🌍 Steuerart:&nbsp;
              {item.is_reverse_charge && <span className="text-emerald-900">Reverse Charge (Inbound)</span>}
              {item.is_intra_community_acquisition && <span className="text-emerald-900">Innergem. Erwerb (Inbound)</span>}
              {!item.is_reverse_charge && !item.is_intra_community_acquisition && item.rc_suspected && (
                <span className="text-amber-900">VERDACHT — bitte prüfen</span>
              )}
              {(item.is_reverse_charge || item.is_intra_community_acquisition) && (
                <span
                  className="text-[10px] bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded font-medium ml-auto"
                  title="Identisch zur UStVA-Buchungslogik"
                >
                  Compliance OK
                </span>
              )}
            </div>
            <div className="space-y-1">
              <FieldRow label="Land Lieferant" value={item.supplier_country} />
              <FieldRow label="USt-IdNr." value={item.supplier_vat_id} />
            </div>

            {/* Inbound RC — § 13b UStG (DE) bzw. § 19 Abs. 1a UStG 1994 (AT) */}
            {item.is_reverse_charge && (
              <div className="text-[11px] text-emerald-900 mt-2 leading-relaxed bg-white border border-emerald-200 rounded px-2.5 py-1.5">
                <div className="font-semibold mb-0.5">📌 Reverse-Charge-Bezug</div>
                <div>
                  Der <strong>Leistungsempfänger</strong> schuldet die USt nach{" "}
                  <strong>§ 13b UStG</strong> (DE) bzw.{" "}
                  <strong>§ 19 Abs. 1a UStG 1994</strong> (AT). Der Beleg weist
                  korrekt 0 % USt aus. Die USt wird beim Empfänger selbst
                  berechnet und in der UStVA{" "}
                  <strong>KZ 46/47</strong> (DE) bzw. UVA-Position{" "}
                  <strong>057/066</strong> (AT) gemeldet, mit gleichzeitigem
                  Vorsteuerabzug.
                </div>
              </div>
            )}

            {/* Inbound IGE — § 1a UStG (DE) bzw. Art. 1 UStG 1994 (AT) */}
            {item.is_intra_community_acquisition && (
              <div className="text-[11px] text-emerald-900 mt-2 leading-relaxed bg-white border border-emerald-200 rounded px-2.5 py-1.5">
                <div className="font-semibold mb-0.5">📌 Innergemeinschaftlicher Erwerb</div>
                <div>
                  Erwerb nach <strong>§ 1a UStG</strong> (DE) bzw.{" "}
                  <strong>Art. 1 Abs. 1 UStG 1994</strong> (AT). Erwerbsteuer
                  19 % in UStVA <strong>KZ 89</strong> (Bemessung 35),
                  gleichzeitig Vorsteuer in <strong>KZ 61</strong>{" "}
                  (Netto-Effekt = 0). AT: UVA-Position{" "}
                  <strong>070/072</strong>.
                </div>
              </div>
            )}

            {item.rc_suspected && !item.is_reverse_charge && !item.is_intra_community_acquisition && (
              <div className="text-[11px] text-amber-900 mt-2 leading-snug">
                ⚠️ Beleg-Analyse vermutet RC oder Innergem. Erwerb (EU-Lieferant, keine USt). Bitte im Edit-Modus bestätigen oder verwerfen — sonst falsch in der Voranmeldung.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  const datesDiffer = form.date && form.paid_at && form.date !== form.paid_at;
  return (
    <div className="space-y-3" data-testid="drawer-expense-edit-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Betrag (brutto)</Label>
          <Input type="number" step="0.01" value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            className="h-8 text-sm rounded-md" data-testid="drawer-edit-amount" />
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Rechnungsdatum</Label>
          <Input type="date" value={form.date}
            onChange={e => {
              const newDate = e.target.value;
              setForm(f => ({
                ...f,
                date: newDate,
                // Wenn paid_at noch dem alten Rechnungsdatum entspricht, ziehe es mit (Default-Verhalten)
                paid_at: f.paid_at === f.date ? newDate : f.paid_at,
              }));
            }}
            className="h-8 text-sm rounded-md" data-testid="drawer-edit-date" />
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">
            Bezahlt am {!datesDiffer && form.paid_at && <span className="text-slate-400 font-normal">(= Rechnungsdatum)</span>}
          </Label>
          <Input type="date" value={form.paid_at || ''}
            onChange={e => setForm(f => ({ ...f, paid_at: e.target.value }))}
            className={`h-8 text-sm rounded-md ${datesDiffer ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
            data-testid="drawer-edit-paid-at" />
          {!form.paid_at && form.date && (
            <button type="button" onClick={() => setForm(f => ({ ...f, paid_at: f.date }))}
              className="text-[10px] text-emerald-600 hover:text-emerald-700 mt-0.5 underline">
              Auf Rechnungsdatum setzen
            </button>
          )}
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Kategorie</Label>
          <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
            <SelectTrigger className="h-8 text-sm rounded-md" data-testid="drawer-edit-category"><SelectValue /></SelectTrigger>
            <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">
            MwSt %
            {Array.isArray(form.tax_breakdown) && form.tax_breakdown.length >= 2 && (
              <span className="ml-1.5 text-[10px] text-amber-700 font-semibold">(Multi-Tax — siehe unten)</span>
            )}
          </Label>
          <Input
            type="number" step="0.1"
            value={form.vat_rate}
            disabled={Array.isArray(form.tax_breakdown) && form.tax_breakdown.length >= 2}
            onChange={e => setForm(f => ({ ...f, vat_rate: parseFloat(e.target.value) || 0 }))}
            className="h-8 text-sm rounded-md" data-testid="drawer-edit-vat" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600 mb-1 block">Beschreibung</Label>
        <Input value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="h-8 text-sm rounded-md" data-testid="drawer-edit-desc" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Händler</Label>
          <Input value={form.vendor}
            onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
            className="h-8 text-sm rounded-md" />
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Rechnungsnr.</Label>
          <Input value={form.invoice_number}
            onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
            className="h-8 text-sm rounded-md" />
        </div>
      </div>

      {/* Feb 2026 — Multi-Tax-Aufschlüsselung als read-only Tabelle im Edit-Mode.
          SB kann die Top-Level-Felder editieren; Multi-Tax-Werte werden im FL-Konto
          korrigiert (z.B. per Beleg-Kommentar). Senden wir aber unverändert zurück.
          Bedingung: STRIKT tax_breakdown.length >= 2 (flaky multi_tax_suspected ignoriert). */}
      {Array.isArray(form.tax_breakdown) && form.tax_breakdown.length >= 2 && (
        <div className="rounded-md border border-amber-300 bg-amber-50/60 p-2.5" data-testid="drawer-edit-multi-tax">
          <div className="text-[11px] text-amber-900 font-semibold mb-1.5 flex items-center gap-1.5">
            <span>⚠</span> Multi-Tax-Aufschlüsselung (read-only)
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-amber-900">
              <tr>
                <th className="text-left font-semibold pb-1">Satz</th>
                <th className="text-right font-semibold pb-1">Netto</th>
                <th className="text-right font-semibold pb-1">MwSt</th>
                <th className="text-right font-semibold pb-1">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {form.tax_breakdown.map((tb, i) => {
                const net = parseFloat(tb.net) || 0;
                const vat = parseFloat(tb.vat) || 0;
                return (
                  <tr key={i} className="border-t border-amber-200/50">
                    <td className="py-0.5 font-medium">{tb.rate}%</td>
                    <td className="py-0.5 text-right tabular-nums">{net.toFixed(2)} €</td>
                    <td className="py-0.5 text-right tabular-nums">{vat.toFixed(2)} €</td>
                    <td className="py-0.5 text-right tabular-nums font-medium">{(net + vat).toFixed(2)} €</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-amber-800 mt-1.5">
            Korrekturen der Aufschlüsselung erfolgen über den FL-Account (per Beleg-Kommentar an den Mandanten).
          </p>
        </div>
      )}

      {/* Feb 2026 — Bewirtungsbescheinigung — SB kann Pflichtfelder direkt nachpflegen */}
      {form.category === "hospitality" && (
        <div className="rounded-md border-2 border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="drawer-edit-hospitality">
          <div className="text-xs font-semibold text-amber-900">
            📋 Bewirtungsbescheinigung (§4 EStG / §20 EStG)
          </div>
          <div>
            <Label className="text-[11px] text-slate-700 mb-0.5 block">Anlass <span className="text-rose-600">*</span></Label>
            <Input
              value={form.hospitality_occasion || ''}
              onChange={e => setForm(f => ({ ...f, hospitality_occasion: e.target.value }))}
              placeholder="z.B. Projektbesprechung Müller GmbH"
              className="h-8 text-sm rounded-md"
              data-testid="drawer-edit-hosp-occasion"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-700 mb-0.5 block">Teilnehmer <span className="text-rose-600">*</span></Label>
            <Textarea
              value={form.hospitality_participants || ''}
              onChange={e => setForm(f => ({ ...f, hospitality_participants: e.target.value }))}
              rows={2}
              placeholder="Eine Person pro Zeile"
              className="text-sm"
              data-testid="drawer-edit-hosp-participants"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-700 mb-0.5 block">Ort <span className="text-rose-600">*</span></Label>
            <Input
              value={form.hospitality_location || ''}
              onChange={e => setForm(f => ({ ...f, hospitality_location: e.target.value }))}
              placeholder="z.B. Restaurant Adler, Berlin"
              className="h-8 text-sm rounded-md"
              data-testid="drawer-edit-hosp-location"
            />
          </div>
        </div>
      )}

      {/* Feb 2026 — Absetzbarer Anteil (SB-Override) mit Quick-Actions.
          Juni 2026 — Parität zum FL-Modal: setzt zusätzlich tax_deductible_type
          (voll/teilweise) für konsistente Behandlung von Privatanteil/Selbstbehalt. */}
      <div className="rounded-md border border-slate-300 bg-slate-50 p-3 space-y-2" data-testid="drawer-edit-deductible">
        <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
          Absetzbarer Anteil <span className="text-slate-400 font-normal">/ Privatanteil</span>
          <span className="text-[10px] font-normal text-slate-500">(SB-Override)</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { val: 100, label: '100% (voll)' },
            { val: 70, label: '70%' },
            { val: 50, label: '50%' },
            { val: 0, label: 'Nicht abziehbar' },
          ].map(({ val, label }) => {
            const active = Number(form.deductible_percentage) === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => setForm(f => ({ ...f, deductible_percentage: val, tax_deductible_type: val >= 100 ? 'full' : 'partial' }))}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                }`}
                data-testid={`drawer-deductible-${val}`}
              >
                {label}
              </button>
            );
          })}
          <div className="flex items-center gap-1 ml-1">
            <Input
              type="number"
              min={0}
              max={100}
              value={form.deductible_percentage ?? ''}
              onChange={e => {
                const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                setForm(f => ({ ...f, deductible_percentage: v, tax_deductible_type: (v === '' || Number(v) >= 100) ? 'full' : 'partial' }));
              }}
              className="h-8 w-20 text-sm rounded-md"
              data-testid="drawer-deductible-input"
            />
            <span className="text-xs text-slate-500">%</span>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 leading-snug">
          {form.category === 'hospitality'
            ? 'Bewirtung: DE i.d.R. 70 %, AT 50 %. '
            : 'Bei gemischter (teils privater) Nutzung den betrieblichen Anteil setzen. '}
          Werte &lt; 100 % werden als <strong>teilweise absetzbar</strong> behandelt und im Periodenabschluss als „SB-korrigierter Wert" gekennzeichnet (§4 EStG / §20 EStG).
        </p>
      </div>

      {/* Feb 2026 — Reverse-Charge / Innergem. Erwerb (Edit-Modus, Mutex zu Bewirtung) */}
      {form.category !== "hospitality" && (() => {
        const isStandard = !form.is_reverse_charge && !form.is_intra_community_acquisition;
        const isRC = !!form.is_reverse_charge;
        const isIGE = !!form.is_intra_community_acquisition;
        return (
          <div
            className={`rounded-md border ${form.rc_suspected && isStandard ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-slate-50"} p-2.5`}
            data-testid="drawer-edit-tax-scheme"
          >
            <div className="text-[11px] font-semibold text-slate-700 mb-1.5">Steuerart</div>
            {form.rc_suspected && isStandard && (
              <div className="text-[10px] text-amber-900 mb-1.5">
                ⚠️ KI-Verdacht: RC oder Innergem. Erwerb. Bitte bestätigen oder als Standard verwerfen.
              </div>
            )}
            <div className="grid grid-cols-3 gap-1.5">
              <label className={`flex flex-col gap-0.5 rounded border p-1.5 cursor-pointer text-[10px] ${isStandard ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
                <input
                  type="radio" checked={isStandard}
                  onChange={() => setForm(f => ({ ...f, is_reverse_charge: false, is_intra_community_acquisition: false }))}
                  data-testid="drawer-tax-scheme-standard"
                />
                <span className="font-medium">Standard</span>
              </label>
              <label className={`flex flex-col gap-0.5 rounded border p-1.5 cursor-pointer text-[10px] ${isRC ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
                <input
                  type="radio" checked={isRC}
                  onChange={() => setForm(f => ({ ...f, is_reverse_charge: true, is_intra_community_acquisition: false, vat_rate: 0 }))}
                  data-testid="drawer-tax-scheme-rc"
                />
                <span className="font-medium">Reverse Charge</span>
              </label>
              <label className={`flex flex-col gap-0.5 rounded border p-1.5 cursor-pointer text-[10px] ${isIGE ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
                <input
                  type="radio" checked={isIGE}
                  onChange={() => setForm(f => ({ ...f, is_reverse_charge: false, is_intra_community_acquisition: true, vat_rate: 0 }))}
                  data-testid="drawer-tax-scheme-intra-eu"
                />
                <span className="font-medium">Innergem. Erwerb</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mt-2 pt-2 border-t border-slate-200">
                <div>
                  <Label className="text-[10px] text-slate-600 mb-0.5 block">Land Lieferant</Label>
                  <Input
                    value={form.supplier_country || ""}
                    onChange={e => setForm(f => ({ ...f, supplier_country: e.target.value.toUpperCase().slice(0, 2) }))}
                    placeholder="IT, FR, NL"
                    maxLength={2}
                    className="h-7 text-xs"
                    data-testid="drawer-rc-supplier-country"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-0.5 block">USt-IdNr.</Label>
                  <Input
                    value={form.supplier_vat_id || ""}
                    onChange={e => setForm(f => ({ ...f, supplier_vat_id: e.target.value.toUpperCase().trim() }))}
                    placeholder="ATU12345678"
                    className="h-7 text-xs"
                    data-testid="drawer-rc-supplier-vat-id"
                  />
                </div>
              </div>
            {!isRC && !isIGE && (() => {
              const cc = (clientCountry || 'DE').toUpperCase();
              const sc = (form.supplier_country || "").toUpperCase();
              const rcSuggested = sc && sc !== cc && (form.supplier_vat_id || "").trim() && (parseFloat(form.vat_rate) || 0) === 0;
              if (!rcSuggested) return null;
              return (
                <div className="mt-2 pt-2 flex items-start justify-between gap-2 rounded border border-amber-300 bg-amber-50 p-2" data-testid="drawer-rc-suggestion-banner">
                  <span className="text-[11px] text-amber-800">Ausländische UID ({sc}) + 0 % USt — meist <strong>Reverse Charge</strong>.</span>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, is_reverse_charge: true, is_intra_community_acquisition: false, vat_rate: 0 }))}
                    className="shrink-0 rounded bg-amber-600 text-white px-2 py-1 text-[10px] font-medium hover:bg-amber-700 transition-colors"
                    data-testid="drawer-rc-suggestion-apply-btn"
                  >
                    Als RC übernehmen
                  </button>
                </div>
              );
            })()}
            {!isRC && !isIGE && (() => {
              const foreign = getForeignRates(form, clientCountry);
              if (!foreign.length) return null;
              const cc = (clientCountry || 'DE').toUpperCase();
              return (
                <div className="mt-2 pt-2 border-t border-slate-200 rounded border border-amber-300 bg-amber-50 p-2" data-testid="drawer-foreign-vat-hint">
                  <p className="text-[11px] text-amber-800">
                    <strong>Ausländischer Steuersatz</strong> ({foreign.map(r => `${r}%`).join(', ')}): nicht als Vorsteuer in der {cc}-Voranmeldung abziehbar — der Bruttobetrag zählt als Betriebsausgabe (ggf. EU-Vorsteuererstattungsverfahren). Falls RC-fähig: korrigierte Rechnung beim Lieferanten anfordern.
                  </p>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {vstpActive && form.category !== "hospitality" && !form.is_reverse_charge && !form.is_intra_community_acquisition && (
        <label
          className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer text-[11px] ${form.vstp_sonder_vorsteuer ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
          data-testid="drawer-vstp-sonder-vorsteuer-toggle"
        >
          <input
            type="checkbox"
            checked={!!form.vstp_sonder_vorsteuer}
            onChange={(e) => setForm(f => ({ ...f, vstp_sonder_vorsteuer: e.target.checked }))}
            className="mt-0.5"
            data-testid="drawer-vstp-sonder-vorsteuer-checkbox"
          />
          <div>
            <div className="font-medium text-slate-800">Wareneinkauf / Fremdleistung (Vorsteuerpauschalierung)</div>
            <div className="text-slate-500 mt-0.5">
              Vorsteuer dieses Belegs ist bei aktiver Pauschalierung <strong>zusätzlich</strong> zur Pauschale abziehbar (§ 14 UStG / § 7 VO).
            </div>
          </div>
        </label>
      )}

      <div>
        <Label className="text-xs text-slate-600 mb-1 block">Notizen</Label>
        <Textarea value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={2} className="text-sm" />
      </div>
    </div>
  );
}

function FieldRow({ label, value, multiline, highlight }) {
  if (value === null || value === undefined || value === '') value = '—';
  return (
    <div className="grid grid-cols-3 gap-2 py-1 border-b border-zinc-100 last:border-0">
      <div className="text-xs text-slate-500 col-span-1">{label}</div>
      <div className={`text-sm col-span-2 ${highlight ? 'text-emerald-700 font-medium' : 'text-slate-800'} ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>{value}</div>
    </div>
  );
}
